import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Form, Grid, Input, Row, Select, theme } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ApplicantQualifyingQuestionsContainerApplicantUserFieldsFragment,
  ApplicantQualifyingQuestionsContainerEntityFieldsFragment,
  ApplicantUserUpdateInput
} from '../../../../generated';
import { HealthProfession } from '../../../../types/healthProfession';
import { HealthProfessionDropdownContainer } from '../../../shared/health-profession-dropdown/health-profession-dropdown-container';
import { getItem, setItem } from '../../../shared/local-storage';
interface QualifyingQuestionsProps {
  applicantUserData: ApplicantQualifyingQuestionsContainerApplicantUserFieldsFragment;
  entitiesData: ApplicantQualifyingQuestionsContainerEntityFieldsFragment[];
  healthProfessionsData: HealthProfession[];
  onSave: (data: ApplicantUserUpdateInput) => void;
}

export const QualifyingQuestions: React.FC<QualifyingQuestionsProps> = (props) => {
  const navigate = useNavigate();
  const [isUpdatingApplicant, setIsUpdatingApplicant] = useState(false);

  const {
    token: { colorBgContainer, borderRadiusLG }
  } = theme.useToken();

  const { applicantUserData, entitiesData } = props;
  const [form] = Form.useForm();
  const screens = Grid.useBreakpoint();

  const [selectedEntities] = React.useState<ApplicantQualifyingQuestionsContainerEntityFieldsFragment[]>(
    applicantUserData.personalInformation?.applyingTo as ApplicantQualifyingQuestionsContainerEntityFieldsFragment[]
  );
  const [formError] = React.useState<string>('');
  const [selectedEntityList, setSelectedEntityList] = React.useState<{ index: number; entityId: string }[]>([]);

  useEffect(() => {
    if (applicantUserData?.id && getItem('applicantUserId') !== applicantUserData.id) {
      setItem('applicantUserId', applicantUserData.id);
    }
    if (applicantUserData?.isProfileSubmitted === true) {
      navigate('/applicant/cases');
    }

    const applyingTo = applicantUserData.personalInformation?.applyingTo;
    if (applyingTo) {
      if (applyingTo.length === 0) {
        // Trigger the add() method to show 1 Entity Select by default
        form.setFieldsValue({ personalInformation: { applyingTo: [{}] } });
      } else {
        let index = 0;
        const tempEntityList: { index: number; entityId: any }[] = [];

        applyingTo.forEach((entity) => {
          tempEntityList.push({ index: index, entityId: entity?.id });
          index++;
        });

        setSelectedEntityList(tempEntityList);
      }
    }
  }, [applicantUserData]);

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        form.submit();

        //applyingTo
        values.personalInformation.applyingTo = selectedEntityList?.map((entity) => {
          return entity.entityId;
        });

        props.onSave(values);
      })
      .catch((error) => {
        if (error.errorFields && error.errorFields.length > 0) {
          const firstErrorField = error.errorFields[0].name;
          form.scrollToField(firstErrorField, {
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
          });
        }
        setIsUpdatingApplicant(false);
        console.log('Form Error', error);
      });
  };

  const onEntitySelectChanged = (selectedEntity: string, index: number) => {
    // Check if index already exists in the list, then update selected entity, else add index and selected entity to the list
    const existingIndex = selectedEntityList.findIndex((entity) => entity.index === index);

    if (existingIndex !== -1) {
      const updatedEntityList = [...selectedEntityList];
      updatedEntityList[existingIndex].entityId = entitiesData.find((entity) => entity.entityName === selectedEntity)?.id;
      setSelectedEntityList(updatedEntityList);
    } else {
      // Insert new item based on the index to keep the list sorted
      const insertionIndex = selectedEntityList.findIndex((entity) => entity.index > index);
      const newSelectedEntityList =
        insertionIndex === -1
          ? [
              ...selectedEntityList,
              {
                index: index,
                entityId: entitiesData.find((entity) => entity.entityName === selectedEntity)?.id
              }
            ]
          : [
              ...selectedEntityList.slice(0, insertionIndex),
              {
                index: index,
                entityId: entitiesData.find((entity) => entity.entityName === selectedEntity)?.id
              },
              ...selectedEntityList.slice(insertionIndex)
            ];

      setSelectedEntityList(newSelectedEntityList);
    }
  };

  const onRemoveEntity = (index: number) => {
    let updatedEntityList = [...selectedEntityList];
    // Remove the selected entity from the list
    const existingIndex = selectedEntityList.findIndex((entity) => entity.index === index);
    if (existingIndex !== -1) {
      updatedEntityList = selectedEntityList.filter((entity) => entity.index !== index);
    }

    // Subtract 1 from the indexes that are greater than the removed index
    updatedEntityList.forEach((entity) => {
      if (entity.index > index) {
        entity.index -= 1;
      }
    });

    setSelectedEntityList(updatedEntityList);
  };

  return (
    <>
      <div
        style={{
          background: colorBgContainer,
          width: screens.xs ? '100%' : '70%',
          minWidth: 280,
          padding: screens.xs ? '24px' : '48px',
          marginTop: '24px',
          borderRadius: borderRadiusLG,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'baseline'
        }}
      >
        <h2>General Information</h2>
        <Form
          form={form}
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start'
          }}
          layout="vertical"
          initialValues={applicantUserData}
        >
          <Form.List
            name={['personalInformation', 'applyingTo']}
            rules={[
              {
                validator: async (_, entities) => {
                  if (!entities || entities.length < 1) {
                    return Promise.reject(new Error('Please add at least one Entity'));
                  }
                }
              }
            ]}
          >
            {(fields, { add, remove }) => {
              return (
                <div style={{ width: window.innerWidth > 768 ? '40%' : '100%' }} >
                  {fields.map((field, index) => {
                    // Get the selected entity ids
                    const selectedEntityIds = selectedEntityList.map((entity) => entity.entityId);

                    return (
                      <Row key={field.key} style={{ marginBottom: '15px' }}>
                        <Col span={2}>
                          {field.key > 0 ? (
                            <MinusCircleOutlined
                              className="dynamic-delete-button"
                              onClick={() => {
                                onRemoveEntity(index);
                                remove(field.name);
                              }}
                            />
                          ) : null}
                        </Col>
                        <Col span={22}>
                          <Form.Item name={[index, 'id']} hidden={true}>
                            <Input hidden={true} />
                          </Form.Item>
                          <Form.Item
                            label="Please select a recipient for the Verification Status Report"
                            name={[index, 'entityName']}
                            validateTrigger={['onChange', 'onBlur']}
                            rules={[
                              {
                                required: true,
                                message: 'Please select at least one Entity.'
                              }
                            ]}
                          >
                            <Select
                              placeholder="Select"
                              style={{ width: '100%', minWidth: 270, maxWidth: 270 }}
                              onChange={(values) => {
                                onEntitySelectChanged(values, index);
                              }}
                            >
                              {entitiesData
                                ?.filter((entity) => !selectedEntityIds.includes(entity.id))
                                .map((item: ApplicantQualifyingQuestionsContainerEntityFieldsFragment) => (
                                  <Select.Option key={item.id} value={item.entityName}>
                                    {item.entityName}
                                  </Select.Option>
                                ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                    );
                  })}
                  {fields.length < 5 ? (
                    <Form.Item>
                      <Button
                        type="dashed"
                        onClick={() => {
                          add();
                        }}
                        block
                      >
                        <PlusOutlined /> Add Additional
                      </Button>
                      {formError && selectedEntities.length < 1 && <p style={{ color: '#ff4d4f' }}>{formError}</p>}
                    </Form.Item>
                  ) : (
                    <></>
                  )}
                </div>
              );
            }}
          </Form.List>
          <HealthProfessionDropdownContainer
            style={{ width: '100%' }}
            placeholder="Select Health Profession"
            name={['personalInformation', 'professional', 'healthProfession']}
            label="Please select your profession of practice."
            rules={[
              {
                required: true,
                message: 'Please select a health profession.'
              }
            ]}
          />
        </Form>
      </div>
      <div
        style={{
          width: screens.xs ? '100%' : '70%',
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '24px 0px'
        }}
      >
        <Button type="primary" onClick={() => onSubmit()} disabled={isUpdatingApplicant}>
          {'Save and Continue >>'}
        </Button>
      </div>
    </>
  );
};
